import { cn } from '@/lib/utils';
import { Button, Label, Switch } from '@/components/atoms';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  SearchBar,
  DatePeriodSelect,
} from '@/components/molecules';
import { ConversationItem } from '@/components/atoms';
import {
  PlusIcon,
  ChevronLeftIcon,
  ChevronsRightIcon,
  UnplugIcon,
} from 'lucide-react';
import type { OmnichannelAccount, ChannelType } from '@/types';
import { PageSelector } from '@/pages/dashboard/conversations/components';
import { useChatbotSettings, useUpdateChatbotSettings } from '@/api';
import { useState } from 'react';

export interface Conversation {
  id: string;
  name: string;
  initials: string;
  lastMessage: string;
  time: string;
  lastMessageAt?: string;
  channel: 'whatsapp' | 'instagram' | 'facebook';
  status: 'hot' | 'warm' | 'cold';
  unread?: number;
  avatar?: string | null;
  responseWindow?: {
    expiresAt: string | null;
    extendedWindowExpiresAt: string | null;
    isExpired: boolean;
    canSendFreeForm: boolean;
  };
}

export interface ConversationListProps {
  conversations: Conversation[];
  selectedConversation?: string;
  searchQuery: string;
  activeTab: string;
  onConversationSelect: (id: string) => void;
  onSearchChange: (query: string) => void;
  onTabChange: (tab: string) => void;
  onConnectChannel: () => void;
  onDisconnectChannel?: (channel: ChannelType) => void;
  channelConnected?: {
    facebook: boolean;
    instagram: boolean;
    whatsapp: boolean;
  };
  isDisconnecting?: boolean;
  conversationCounts?: {
    all: number;
    facebook: number;
    instagram: number;
    whatsapp: number;
  };
  accounts?: OmnichannelAccount[];
  selectedPageId?: string;
  onPageSelect?: (pageId: string) => void;
  isLoadingAccounts?: boolean;
  className?: string;
}

function ConversationList({
  conversations,
  selectedConversation,
  searchQuery,
  activeTab,
  onConversationSelect,
  onSearchChange,
  onTabChange,
  onConnectChannel,
  onDisconnectChannel,
  channelConnected,
  isDisconnecting = false,
  conversationCounts,
  accounts,
  selectedPageId,
  onPageSelect,
  isLoadingAccounts,
  className,
  ...props
}: ConversationListProps) {
  const currentChannel =
    activeTab !== 'all' ? (activeTab as ChannelType) : null;
  const isCurrentChannelConnected =
    currentChannel && channelConnected?.[currentChannel];
  const [timeFilter, setTimeFilter] = useState('7days');
  const { data: chatbotSettings, isLoading: isLoadingChatbotSettings } =
    useChatbotSettings();
  const updateChatbotSettings = useUpdateChatbotSettings();

  const handleAutoFollowUpChange = (checked: boolean) => {
    if (!chatbotSettings) return;

    updateChatbotSettings.mutate({
      personality: chatbotSettings.personality,
      enableAutoFollowUp: checked,
      enableAutoSuggestion: chatbotSettings.enableAutoSuggestion,
      followUpMessage: chatbotSettings.followUpMessage,
      scoreCustomization: chatbotSettings.scoreCustomization,
      coldLeadDelayTime: chatbotSettings.coldLeadDelayTime,
    });
  };

  return (
    <div
      className={cn(
        'w-full lg:max-w-lg bg-white h-full flex flex-col rounded-md',
        className
      )}
      {...props}
    >
      {/* Header */}
      <div className="p-4 flex justify-between items-center content-center gap-4">
        <div className="flex-1 max-w-md">
          <SearchBar
            searchQuery={searchQuery}
            setSearchQuery={onSearchChange}
            placeholder="Search chat"
            showFilter={false}
            showExport={false}
          />
        </div>
        <div className="flex items-center gap-2">
          {isCurrentChannelConnected &&
            onDisconnectChannel &&
            currentChannel && (
              <Button
                size="sm"
                variant="outline"
                className="border-destructive text-destructive hover:bg-destructive/10"
                onClick={() => onDisconnectChannel(currentChannel)}
                disabled={isDisconnecting}
              >
                <UnplugIcon className="w-4 h-4 mr-2" />
                Disconnect
              </Button>
            )}
          <Button size="sm" className="" onClick={onConnectChannel}>
            <PlusIcon className="w-4 h-4 mr-2" />
            Connect Channel
          </Button>
        </div>
      </div>

      {accounts && accounts.length > 0 && onPageSelect && (
        <div className="p-4 w-full border-b border-gray-200">
          <PageSelector
            accounts={accounts}
            selectedPageId={selectedPageId}
            onPageSelect={onPageSelect}
            isLoading={isLoadingAccounts}
          />
        </div>
      )}

      {/* Channel Tabs */}
      <div className="p-4 border-b border-gray-200">
        <Tabs value={activeTab} onValueChange={onTabChange}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="all" count={conversationCounts?.all || 0}>
              All
            </TabsTrigger>
            <TabsTrigger
              value="whatsapp"
              count={conversationCounts?.whatsapp || 0}
            >
              WhatsApp
            </TabsTrigger>
            <TabsTrigger
              value="instagram"
              count={conversationCounts?.instagram || 0}
            >
              Instagram
            </TabsTrigger>
            <TabsTrigger
              value="facebook"
              count={conversationCounts?.facebook || 0}
            >
              Facebook
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div
        className="p-4 border-b border-gray-200"
        data-tour="ai-auto-followup"
      >
        <div className="flex items-center justify-between gap-4">
          <DatePeriodSelect
            value={timeFilter}
            onValueChange={setTimeFilter}
            triggerClassName="w-40 border-none bg-muted/30"
          />

          <div className="flex items-center gap-3">
            <Switch
              checked={chatbotSettings?.enableAutoFollowUp ?? false}
              onCheckedChange={handleAutoFollowUpChange}
              disabled={
                isLoadingChatbotSettings ||
                !chatbotSettings ||
                updateChatbotSettings.isPending
              }
            />
            <Label className="cursor-pointer text-sm font-normal">
              Enable AI Auto Follow-up
            </Label>
          </div>
        </div>
      </div>

      {/* Conversation List */}
      <div className="flex-1 h-full max-h-[55vh] overflow-auto">
        {conversations.map((conversation) => (
          <ConversationItem
            key={conversation.id}
            {...conversation}
            isSelected={selectedConversation === conversation.id}
            onClick={() => onConversationSelect(conversation.id)}
          />
        ))}
      </div>

      {/* Pagination */}
      <div className="p-4">
        <div className="flex items-center justify-center gap-3">
          <Button
            size="sm"
            className="w-10 h-10 rounded-lg bg-primary text-white hover:bg-primary/90"
          >
            1
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="w-10 h-10 rounded-lg bg-primary/5 text-primary hover:bg-primary/10 border-none"
          >
            <ChevronLeftIcon className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="w-10 h-10 rounded-lg bg-primary/5 text-primary hover:bg-primary/10 border-none"
          >
            <ChevronsRightIcon className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export { ConversationList };
